import React, { useContext, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import ProjectHeader from './ProjectHeader';
import ProjectMedia from './ProjectMedia';
import { ProjectContext } from './Admin/ProjectContext';

const Project = () => {
  const { projects, selectedProject, setSelectedProject, loading, error } = useContext(ProjectContext);
  const { id } = useParams(); // Get project ID from URL
  const containerRef = useRef(null);
  const logoRef = useRef(null);

  // Set selected project based on URL param
  useEffect(() => {
    if (id && projects.length > 0) {
      const project = projects.find((p) => p.id === parseInt(id));
      if (project) {
        setSelectedProject(project);
      } else {
        console.error(`Project with ID ${id} not found`);
      }
    }
  }, [id, projects, setSelectedProject]);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      const logo = logoRef.current;

      if (container && logo) {
        const containerRect = container.getBoundingClientRect();
        const scrollProgress = -containerRect.top / window.innerHeight;

        // Calculate logo position based on container center
        const logoY = scrollProgress * window.innerHeight * 0.05;
        logo.style.transform = `translate(-50%, calc(-50% + ${logoY}px))`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return <p className="text-center text-gray-600 pt-36">Loading project...</p>;
  }

  if (error) {
    return <p className="text-center text-red-600 pt-36">Error: {error}</p>;
  }

  if (!selectedProject) {
    return <p className="text-center text-gray-600 pt-36">No project found.</p>;
  }

  // Map project data to component format
  const projectData = {
    name: selectedProject.title || 'Untitled Project',
    year: selectedProject.year?.toString() || 'Unknown Year',
    websiteUrl: selectedProject.link || null,
    location: selectedProject.location || 'Unknown Location',
    techStack: selectedProject.tech_stack || [],
    collaborators:
      selectedProject.collaborators?.map((comp) => ({
        name: comp.name || 'Unknown',
        role: comp.description || 'Contributor', // Map description to role
      })) || [],
    description:
      selectedProject.description?.find((block) => block.type === 'text')?.content ||
      'No description available',
    logobackground: selectedProject.logo_background || 'https://via.placeholder.com/1600x670',
    logo: selectedProject.logo || 'https://via.placeholder.com/150',
    media:
      selectedProject.description?.filter((item) => item.type === 'image' || item.type === 'video').map((block) => ({
        type: block.type,
        url: block.url || null,
        description: block.description || '',
      })) || [],
  };

  // Find previous and next projects
  const sortedProjects = [...projects].sort((a, b) => a.id - b.id); // Sort by ID
  const currentIndex = sortedProjects.findIndex((p) => p.id === parseInt(id));
  const previousProject = currentIndex > 0 ? {
    title: sortedProjects[currentIndex - 1].title || 'Previous Project',
    urlId: sortedProjects[currentIndex - 1].id
  } : null;
  const nextProject = currentIndex < sortedProjects.length - 1 ? {
    title: sortedProjects[currentIndex + 1].title || 'Next Project',
    urlId: sortedProjects[currentIndex + 1].id
  } : null;

  return (
    <>
      <div className="mt-9" />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Project Header */}
        <ProjectHeader project={projectData} />
        {/* Parallax Logo Section */}
        <div
          ref={containerRef}
          className="relative h-screen overflow-hidden"
        >
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${projectData.logobackground})`,
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-30" />
            {/* Centered Logo Container */}
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                ref={logoRef}
                src={projectData.logo}
                alt={projectData.name}
                className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2
                       max-w-[min(60vw,150px)] max-h-[min(60vh,500px)]
                       w-auto h-auto object-contain transition-transform duration-200 ease-out"
                style={{
                  willChange: 'transform',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        </div>

        {/* Project Description */}
        <section className="my-24 text-justify mx-auto">
          <p className="text-2xl leading-relaxed text-gray-600">
            {projectData.description}
          </p>
        </section>
      </div>
      {/* Project Media */}
      {projectData.media?.map((mediaItem, index) => (
        <ProjectMedia
          key={index}
          media={mediaItem}
          index={index}
        />
      ))}

      {/* Project Navigation */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          {nextProject && (
            <Link
              to={`/projects/${nextProject.urlId}`}
              className="group relative flex items-center gap-2 text-gray-500 hover:text-black transition-colors duration-200 text-lg pb-1"
            >
              <span className="transform transition-transform duration-500 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)] text-[1.1em] group-hover:-translate-x-1.5 -translate-y-0.5">
                ←
              </span>
              <span className="font-light text-base sm:text-base">{nextProject.title}</span>
              <span className="absolute bottom-0 right-0 w-0 h-[0.8px] bg-gray-500 transition-all duration-300 group-hover:w-10/12"></span>
            </Link>
          )}
          <div className="flex-grow"></div> {/* Spacer */}
          {previousProject && (
            <Link
              to={`/projects/${previousProject.urlId}`}
              className="group relative flex items-center gap-2 text-gray-500 hover:text-black transition-colors duration-200 text-lg pb-1"
            >
              <span className="font-light text-base sm:text-base">{previousProject.title}</span>
              <span className="transform transition-transform duration-500 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)] text-[1.1em] group-hover:translate-x-1.5 -translate-y-0.5">
                →
              </span>
              <span className="absolute bottom-0 left-0 w-0 h-[0.8px] bg-gray-400 transition-all duration-300 group-hover:w-10/12"></span>
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default Project;